import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getAssetsList, importAssetsCsv } from '@/apis/common'
import { useApiReady } from '@/hooks/useApiReady'

// Query keys
export const devicesToolsKeys = {
  all: ['devicesTools'] as const,
  lists: () => [...devicesToolsKeys.all, 'list'] as const,
  list: (filters: any) => [...devicesToolsKeys.lists(), filters] as const,
}

interface DevicesToolsFilters {
  page?: number
  limit?: number
  asset_type?: 'device' | 'tool'
  status?: string
  freelancer_id?: string
  search?: string
  team_id?: string
}

function devicesToolsListQueryKey(filters: DevicesToolsFilters) {
  return devicesToolsKeys.list({
    page: filters.page,
    limit: filters.limit,
    activeTab: filters.asset_type,
    status: filters.status,
    freelancerFilter: filters.freelancer_id,
    searchValue: filters.search,
    teamId: filters.team_id,
  })
}

function buildDevicesToolsQueryFn(filters: DevicesToolsFilters) {
  return async () => {
    if (filters.team_id === null) {
      return { items: [], total: 0 }
    }

    const params: Record<string, unknown> = {
      page: filters.page,
      limit: filters.limit,
    }

    if (filters.asset_type) {
      params.asset_type = filters.asset_type
    }
    if (filters.status) {
      params.status = filters.status
    }
    if (filters.freelancer_id) {
      params.freelancer_id = filters.freelancer_id
    }
    if (filters.search) {
      params.search = filters.search
    }
    if (filters.team_id) {
      params.team_id = filters.team_id
    }

    const res = await getAssetsList(params)

    if (res.data.code === 200) {
      let items = res.data.data.items || []

      items.sort((a, b) => {
        if (a.status === 'assigned' && b.status !== 'assigned') return -1
        if (a.status !== 'assigned' && b.status === 'assigned') return 1

        const getDate = (item: { assigned_on?: string; returned_on?: string }) => {
          if (item.assigned_on) return new Date(item.assigned_on)
          if (item.returned_on) return new Date(item.returned_on)
          return new Date(0)
        }

        const dateA = getDate(a)
        const dateB = getDate(b)
        return dateB.getTime() - dateA.getTime()
      })

      return {
        items,
        total: res.data.data.total || 0,
      }
    }

    return { items: [], total: 0 }
  }
}

export function getDevicesToolsQueryOptions(filters: DevicesToolsFilters) {
  return {
    queryKey: devicesToolsListQueryKey(filters),
    queryFn: buildDevicesToolsQueryFn(filters),
  }
}

/**
 * Hook pour récupérer la liste des devices et tools
 */
export function useDevicesTools(filters: DevicesToolsFilters, options?: { enabled?: boolean }) {
  const apiReady = useApiReady()
  return useQuery({
    ...getDevicesToolsQueryOptions(filters),
    enabled: apiReady && options?.enabled !== false && filters.team_id !== null,
  })
}

/**
 * Hook pour importer des devices/tools en batch depuis un CSV
 */
export function useImportAssetsCsv() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assets: Array<{
      asset_type: string
      tool_name: string
      serial_number?: string
      freelancer_email: string
      status?: string
      assigned_on?: string
      returned_on?: string
    }>) => {
      const res = await importAssetsCsv({ assets })
      if (res.data.code === 200) {
        return res.data.data
      }
      throw new Error(res.data.message || 'Failed to import assets')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devicesToolsKeys.all })
    },
    onError: (error: Error) => {
      console.error('Failed to import assets:', error)
    },
  })
}
